import React from 'react';
import { i18n } from 'next-i18next';
import { ThemeProvider, fontFamilies } from '@sy/component-library';
import WrapWithProviders from '@sy/app-client/src/ssrAdapter/ssrWrapper';
import Footer from '@sy/app-client/src/ssrAdapter/components/Footer';
import LandingHeader from '@sy/app-client/src/ssrAdapter/components/LandingHeader';
import { getNewBrandingState } from '@sy/app-client/src/ssrAdapter/services/newBranding';
import type { CacheNavigation } from '@sy/ssr-types';

import type { FeatureFlags } from '~/getServerSideProps/contextDecorators/withFeatureFlags';

import { Layout, Top, TopPadding } from './MarketingTemplate.styles';
import WithGoogleOneTap from './WithGoogleOneTap';

const MarketingTemplate = ({
	activePage,
	children,
	featureFlags,
	navigation,
	overflowVisible,
	topPadding = true,
	user,
	logoVersion,
	useDarkNewBrandingSurface,
}: Props) => {
	const branding = getNewBrandingState({
		logoVersion: logoVersion ?? featureFlags.orion.logoExperimentVariant,
		useDarkNewBrandingSurface,
	});
	const shouldRenderTopPadding = topPadding && !branding.isNewBrandingEnabled;

	return (
		<WrapWithProviders i18n={i18n} user={user} withTheme={false}>
			<ThemeProvider
				themeName={branding.useDarkNewBrandingSurface ? 'dark' : 'marketing'}
				isGlobal
				isCustom={branding.useDarkNewBrandingSurface}
				fontFamily={
					branding.shouldUseNewBrandingTypography
						? fontFamilies.satoshi
						: undefined
				}
			>
				<>
					<WithGoogleOneTap />
					<Layout
						overflowVisible={overflowVisible}
						$useDarkNewBrandingSurface={branding.useDarkNewBrandingSurface}
					>
						<Top>
							<LandingHeader
								activePage={activePage}
								header={navigation?.header}
								useDarkNewBrandingSurface={branding.useDarkNewBrandingSurface}
								logoVersion={branding.logoVersion}
							/>
							{shouldRenderTopPadding && <TopPadding />}
							{children}
						</Top>
					</Layout>
					<Footer
						logoVersion={branding.logoVersion}
						useDarkNewBrandingSurface={branding.useDarkNewBrandingSurface}
					/>
				</>
			</ThemeProvider>
		</WrapWithProviders>
	);
};

interface Props {
	activePage?: string;
	children: React.ReactNode;
	featureFlags: FeatureFlags;
	overflowVisible?: boolean;
	navigation?: CacheNavigation | null;
	topPadding?: boolean;
	user: { email: string | null; id: string | null };
	logoVersion?: 'control' | 'treatment1' | 'treatment2';
	useDarkNewBrandingSurface?: boolean;
}

export default MarketingTemplate;
